import React, { useState, useCallback, useContext } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Tooltip, Upload, message } from 'antd';
import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import { GridContainer, GridItem, DownloadButton, PinNavBar, ComplexContainer } from '../ComplexDocumentDesign/MenuBar/styles';
import ZoomDropdown from '../ComplexDocumentDesign/ZoomDropdown';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { useEmailContext, COMPLEX_EMAIL_CONTEXT_KEYS } from '../../context/complexEmailContext';
import { deepCopy, toZipFile } from '../../utils/javascript';
import {
  DYNAMIC_TABLE_BLOCK,
  FOOTER,
  HEADER,
  STANDALONE_BLOCK,
  STATIC_TABLE_BLOCK,
  TEXT_BLOCK,
} from '../../utils/complexDocumentConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { ENTERPRISE_ENV } from '../../utils/helper';
import {
  modifyBlockContents,
  modifyDynamicTableBlockContents,
  modifyTextBlockContents,
  modifyVariableContract,
  extractVariables,
  listAllUsedFonts,
  EXPORT_FILE_NAME,
  importVariables,
  partialMatchKey,
  modifyStaticTableBlockContents,
  extractRelationship,
  exportRelations,
  importRelationships
} from '../../utils/importHelper';
import { FontsContext } from '../../context/fontsContext';
import { useSessionContext } from '../../context/sessionContext';
import axiosCall from '../../utils/services/interceptor';
import { uploadFonts } from '../../utils/services/uploadAPI';
import { patchEmailSubject } from '../../utils/services/complexEmailAPI';
import { v4 } from 'uuid';
import { uploadGoogleFonts } from '../../utils/services/fontsAPI';
import { EMAIL_TEMPLATES_MENUBAR } from './translationValues';
import { getAllLanguage } from '../../utils/services/languageApi';
import { changeLanguage } from '../../utils/services/complexDocumentsAPI';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';

const NAMESPACES = ['EMAIL_TEMPLATES_MENUBAR'];

const checkUserAccess = (userType, roles, userDetails = {}) => {
  if (roles) {
    return (
      userDetails?.userType === userType && userDetails.roles.some(role => roles.includes(role))
    );
  } else {
    return userDetails?.userType === userType;
  }
};

const MenuBar = props => {
  const { showVersions } = props;

  const [isPinned, setIsPinned] = useState(false);
  const [isDropDownVisible, toggleDropDownVisible] = useState(false);
  const { fontMeta, handleFonts } = useContext(FontsContext);
  const { t } = useTranslation(NAMESPACES);

  const contextSelector = useCallback(store => store, []);
  const [documentContext, store] = useDocumentContext(contextSelector);

  const customSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable, updateArtboard] = useArtboardContext(customSelector);

  const currentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK] && store[CONTEXT_KEYS.CURRENT_BLOCK] !== '', []);
  const [currentBlock] = useArtboardContext(currentBlockSelector, `ComplexEmailMenuBar`);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const emailSelector = useCallback(store => null, []);
  const [, emailStore] = useEmailContext(emailSelector, 'menuBar');

  const getDefaultStyleSelector = useCallback(store => store, []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const toggleIsPinned = useCallback(() => {
    setIsPinned(previousValue => !previousValue);
  }, []);

  const exportTemplate = async () => {
    const flowIds = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
    const globalVariables = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
    const variablesToExport = new Set();
    const relationships = [];
    updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });
    try {
      const exportedFlows = flowIds.map(flowId => {
        const contextFlow = documentContext[flowId];
        const {
          margin,
          width,
          height,
          backgroundColor,
          pageNumber,
          size,
          orientation,
        } = contextFlow;
        const blockIds = contextFlow[`${flowId}-0`];
        const exportedBlocks = blockIds.flatMap(blockId => {
          const block = deepCopy(documentContext[blockId]);

          if (block.blockType === STANDALONE_BLOCK) {
            const standaloneBlocks = block.contents.map(contentUUID => {
              const block = deepCopy(documentContext[contentUUID]);
              extractVariables(block.variableContract, globalVariables).forEach(v => {
                variablesToExport.add(v);
              });
              const extractedRelationships = extractRelationship(block.variableContract, globalVariables);
              relationships.push(...extractedRelationships);
              delete block.blockId;
              delete block.orderNumber;
              block.isStandalone = false;
              if (block.blockType === DYNAMIC_TABLE_BLOCK) {
                const tableProperties = block.style.tableProperties;
                const tableChildBlock = [tableProperties.topBlock, tableProperties.bottomBlock];
                const dtoList = tableChildBlock.filter(element => element !== undefined);
                block.blockDTOList = dtoList.map(childBlockUUID => {
                  const childBlock = deepCopy(documentContext[childBlockUUID]);
                  extractVariables(childBlock.variableContract, globalVariables).forEach(v => {
                    variablesToExport.add(v);
                  });
                  childBlock.isStandalone = false;
                  return childBlock;
                });
              }
              return block;
            });
            return standaloneBlocks;
          } else if (block.blockType === DYNAMIC_TABLE_BLOCK) {
            const tableProperties = block.style.tableProperties;
            const tableChildBlock = [tableProperties.topBlock, tableProperties.bottomBlock];
            const dtoList = tableChildBlock.filter(element => element !== undefined);
            block.blockDTOList = dtoList.map(childBlockUUID => {
              const childBlock = deepCopy(documentContext[childBlockUUID]);
              extractVariables(childBlock.variableContract, globalVariables).forEach(v => {
                variablesToExport.add(v);
              });
              childBlock.isStandalone = false;
              return childBlock;
            });
          } else if (block.blockType === STATIC_TABLE_BLOCK) {
            block.contents = block.contents.map(content => {
              if (documentContext[content.uuid]) {
                content.contents = [documentContext[content.uuid]];
              }
              return content;
            })
          }
          extractVariables(block.variableContract, globalVariables).forEach(v => {
            variablesToExport.add(v);
          });
          const extractedRelationships = extractRelationship(block.variableContract, globalVariables);
          relationships.push(...extractedRelationships);
          return block;
        });
        let exportedHeader, exportedFooter;
        if (contextFlow[HEADER]) {
          const header = deepCopy(documentContext[contextFlow[HEADER]]);
          header.isStandalone = false;
          exportedHeader = header;
          extractVariables(header.variableContract, globalVariables).forEach(v =>
            variablesToExport.add(v),
          );
        }
        if (contextFlow[FOOTER]) {
          const footer = deepCopy(documentContext[contextFlow[FOOTER]]);
          footer.isStandalone = false;
          exportedFooter = footer;
          extractVariables(footer.variableContract, globalVariables).forEach(v =>
            variablesToExport.add(v),
          );
        }
        const exportedFlow = {
          style: {
            padding: {
              top: margin[0],
              right: margin[1],
              bottom: margin[2],
              left: margin[3],
            },
            width: width,
            height: height,
            backgroundColor: backgroundColor,
            pageNumber: pageNumber ? { ...documentContext[pageNumber] } : undefined,
          },
          paperSize: size,
          paperOrientation: orientation,
          blocks: exportedBlocks,
          header: exportedHeader,
          footer: exportedFooter,
        };
        return exportedFlow;
      });
      const relationshipsToExport = await exportRelations(relationships);

      // create zip file with json template and fonts.
      const fonts = { nonGoogleFonts: [], googleFonts: [] };
      const fontObject = {};
      const usedFontList = listAllUsedFonts();
      for (let i = 0; i < fontMeta.length; i++) {
        const font = fontMeta[i];
        if (usedFontList.includes(font.name) && font.fontType === 'uploaded-font') {
          for (let j = 0; j < font.fontFiles.length; j++) {
            const file = font.fontFiles[j];
            if (file.fontOrigin === 'upload-font') {
              const rData = { responseType: 'arraybuffer' };
              const { data } = await axiosCall('get', file.fileLink, rData, true);
              const filePathArr = file.filePath?.split(".");
              const fileExtension = filePathArr?.length ? filePathArr[filePathArr.length - 1] : 'ttf';
              const fontName = `${font.name}-${file.fontType}.${fileExtension}`;
              fontObject[fontName] = new Uint8Array(data);
              fonts.nonGoogleFonts.push(fontName);
            } else {
              const googleUploadFont = {
                file: file.fileLink,
                fontName: font.name,
                fontType: file.fontType,
              };
              fonts.googleFonts.push(googleUploadFont);
            }
          }
        }
      }
      const emailCurrentStore = emailStore.get();
      const subject = emailCurrentStore[COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT];
      if (subject) {
        extractVariables(subject.variableContract, globalVariables).forEach(v =>
          variablesToExport.add(v),
        );
      }

      const languagecode = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE];
      let language;
      if (languagecode) {
        try {
          const languageResponse = await getAllLanguage();
          if (languageResponse.data) {
            language = languageResponse.data.find(list => list.code === languagecode);
          }
        } catch (e) {
          console.log(e);
        }
      }; 
      const flowStyle = documentContext[flowIds[0]];

      const style = {
        backgroundColor: flowStyle.backgroundColor,
        color: flowStyle.color,
        margin: flowStyle.margin,
        width: flowStyle.width,
        fontFamily: documentStyle.fontFamily,
        fontSize: documentStyle.fontSize,
      };

      const documentToExport = {
        flows: exportedFlows,
        style: style,
        variables: [...variablesToExport],
        relationships: relationshipsToExport,
        fonts: fonts,
        subject: subject,
        language:language
      };

      const content = zipSync({ [EXPORT_FILE_NAME]: strToU8(JSON.stringify(documentToExport)), 'fonts': fontObject });
      toZipFile(content, documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]);
    } catch {
      message.error(t(
        EMAIL_TEMPLATES_MENUBAR.EMAIL_EXPORT_ERROR.key,
        EMAIL_TEMPLATES_MENUBAR.EMAIL_EXPORT_ERROR.value,
      ));
    } finally {
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
    }
  };

  const importTemplate = file => {
    const reader = new FileReader();
    reader.onload = handleTemplateImport;
    reader.readAsArrayBuffer(file);
  };

  const handleTemplateImport = async event => {
    let hasError = false;
    try {
      // unzip import file and get the contents
      const zipFile = unzipSync(new Uint8Array(event.target.result));
      const templateFile = strFromU8(zipFile[partialMatchKey(zipFile, EXPORT_FILE_NAME)]);
      const parsedFile = JSON.parse(templateFile);
      const { flows, variables, style, fonts, subject, language, relationships } = parsedFile;

      if (subject) {
        subject.blockId = undefined;
        const response = await patchEmailSubject(
          documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
          subject,
        );
        if (response.statusCode === 3109) {
          emailStore.set({
            [COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT]: Object.assign({}, subject, {
              blockId: subject.blockId === undefined ? response.data.id : subject.blockId,
            }),
          });
        }
      }

      const globalVariables = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });
      await importRelationships(relationships);
      const variableIdMap = await importVariables(variables, globalVariables, store);
      const uploadedUrlsMap = new Map();
      for (let flowIndex = 0; flowIndex < flows.length; flowIndex++) {
        const flow = flows[flowIndex];
        flow.flowId = undefined;
        flow.isStandalone = false;
        const blocks = flow.blocks;
        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
          const block = blocks[blockIndex];
          block.blockId = undefined;
          block.uuid = v4();
          if (block.type === DYNAMIC_TABLE_BLOCK) {
            for (let dIndex = 0; dIndex < block.blockDTOList?.length; dIndex++) {
              const subBlock = block.blockDTOList[dIndex];
              subBlock.contents = await modifyBlockContents(subBlock.contents, variableIdMap, uploadedUrlsMap);
              subBlock.variableContract = modifyVariableContract(
                subBlock.variableContract,
                variableIdMap,
              );
            }
            block.contents = modifyDynamicTableBlockContents(block.contents, variableIdMap);
          } else if (block.type === TEXT_BLOCK) {
            block.contents = await modifyTextBlockContents(block.contents, variableIdMap);
          } else if (block.type === STATIC_TABLE_BLOCK) {
            block.contents = await modifyStaticTableBlockContents(block.contents, variableIdMap, uploadedUrlsMap);
          } else {
            block.contents = await modifyBlockContents(block.contents, variableIdMap, uploadedUrlsMap);
          }
          block.variableContract = modifyVariableContract(block.variableContract, variableIdMap);
        }
      }

      // upload non-google fonts
      const nonGoogleFonts = fonts?.nonGoogleFonts || [];
      for (let fIndex = 0; fIndex < nonGoogleFonts.length; fIndex++) {
        const fontFile = new Blob([zipFile[partialMatchKey(zipFile, `fonts/${nonGoogleFonts[fIndex]}`)]]);
        await uploadFonts(fontFile, nonGoogleFonts[fIndex]);
      }
      // upload google fonts
      const googleFonts = fonts?.googleFonts || [];
      const googleFontsPayload = { fonts: [] };
      for (let fIndex = 0; fIndex < googleFonts.length; fIndex++) {
        googleFontsPayload.fonts.push(googleFonts[fIndex]);
      }
      if (googleFontsPayload.fonts.length > 0) {
        await uploadGoogleFonts(googleFontsPayload);
      }
      handleFonts(); // refetch all the fonts and append the font styles to head.

      setTimeout(() => {
        try {
          const callback = async (status = 'failed') => {
            if (status === 'success' && language) {
              try {
                const payload = {
                  id: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
                  language: language,
                  type: 'email',
                };
               const response =  await changeLanguage(payload);
              } catch (e) {
                console.log(e);
              }
            }
          };
          store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: callback});
          const flowUUID = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0];
          store.setPage('replaceBlocks', {
            uuid: flowUUID,
            blocks: flows[0].blocks,
            style: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
            flowStyle: style,
          });
        } catch (e) {
          message.error(t(
            EMAIL_TEMPLATES_MENUBAR.EMAIL_IMPORT_ERROR.key,
            EMAIL_TEMPLATES_MENUBAR.EMAIL_IMPORT_ERROR.value,
          ));
        } finally {
          updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
        }
      }, 1000);
    } catch (e) {
      hasError = true;
      message.error(t(
        EMAIL_TEMPLATES_MENUBAR.EMAIL_IMPORT_ERROR.key,
        EMAIL_TEMPLATES_MENUBAR.EMAIL_IMPORT_ERROR.value,
      ));
    } finally {
      if (hasError) {
        updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
      }
    }
  };

  return (
    <>
      <ComplexContainer>
      {!showVersions && (
        <GridContainer
          complexMenubar={ENTERPRISE_ENV}
          className={`${!currentBlock && 'enable'} ${(isPinned  || isDropDownVisible) && !currentBlock ? 'visible' : ''}`}
        >
          <PinNavBar>
            <div>
              <span className={isPinned ? 'pinned' : ''} onClick={toggleIsPinned}>
                <svg viewBox="0 0 32 32">
                  <g id="surface1">
                    <path
                      style={{}}
                      d="M 20.53125 2.5625 L 19.84375 3.5 L 14.9375 10.1875 C 12.308594 9.730469 9.527344 10.472656 7.5 12.5 L 6.78125 13.1875 L 12.09375 18.5 L 4 26.59375 L 4 28 L 5.40625 28 L 13.5 19.90625 L 18.8125 25.21875 L 19.5 24.5 C 21.527344 22.472656 22.269531 19.691406 21.8125 17.0625 L 28.5 12.15625 L 29.4375 11.46875 Z M 20.78125 5.625 L 26.375 11.21875 L 20.15625 15.78125 L 19.59375 16.1875 L 19.78125 16.84375 C 20.261719 18.675781 19.738281 20.585938 18.59375 22.1875 L 9.8125 13.40625 C 11.414063 12.261719 13.324219 11.738281 15.15625 12.21875 L 15.8125 12.40625 L 16.21875 11.84375 Z "
                    />
                  </g>
                </svg>
              </span>
            </div>
          </PinNavBar>
          <GridItem enterprise={ENTERPRISE_ENV}>
             {(ENTERPRISE_ENV || checkUserAccess('master', undefined, userDetails)) && (
              <>
                <Tooltip title={t(
                      EMAIL_TEMPLATES_MENUBAR.EXPORT_EMAIL_LABEL.key,
                      EMAIL_TEMPLATES_MENUBAR.EXPORT_EMAIL_LABEL.value,
                    )}>
                  <DownloadButton
                    icon="export"
                    type="primary"
                    size="large"
                    onClick={exportTemplate}
                  />
                </Tooltip>
                {editable ? (
                    <Upload accept=".zip" action={importTemplate}>
                      <Tooltip
                        title={t(
                          EMAIL_TEMPLATES_MENUBAR.IMPORT_EMAIL_LABEL.key,
                          EMAIL_TEMPLATES_MENUBAR.IMPORT_EMAIL_LABEL.value,
                        )}
                      >
                        <DownloadButton type="primary" size="large" icon="import" />
                      </Tooltip>
                    </Upload>
                  ) : null}
              </>
            )}
            <ZoomDropdown dropDownVisible={toggleDropDownVisible} />
          </GridItem>
        </GridContainer>
      )}
      </ComplexContainer>
    </>
  );
};

export default withTranslation(NAMESPACES)(MenuBar);
